import { getToken } from '../../utils/storage';

const getApiBase = () => {
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return '/api';
  }
  return (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
};
const API_BASE = getApiBase();

/**
 * Onboarding API client — uses JWT when the intern is logged in.
 */
export class OnboardingError extends Error {
  constructor(message, { status, payload } = {}) {
    super(message);
    this.name = 'OnboardingError';
    this.status = status;
    this.payload = payload || {};
  }
}

const FALLBACK_MESSAGE =
  'We could not reach the server just then. Your answers are safe — please try again in a moment.';

async function request(method, endpoint, { body, formData, signal } = {}) {
  const options = { method, headers: {}, signal, credentials: 'include' };

  const token = getToken();
  if (token) {
    options.headers.Authorization = `Bearer ${token}`;
  }

  if (formData) {
    options.body = formData;
  } else if (body !== undefined) {
    options.headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetch(`${API_BASE}/onboarding${endpoint}`, options);
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new OnboardingError(FALLBACK_MESSAGE, { status: 0 });
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok || payload?.success === false) {
    throw new OnboardingError(payload?.message || FALLBACK_MESSAGE, {
      status: response.status,
      payload: payload || {},
    });
  }

  return payload;
}

export const onboardingApi = {
  getConfig: () => request('GET', '/config'),

  start: (campusId) => request('POST', '/start', { body: { campus_id: campusId || null } }),

  resume: (token) => request('GET', `/resume/${encodeURIComponent(token)}`),

  attachCampus: (token, campusId) =>
    request('POST', '/attach-campus', { body: { draft_token: token, campus_id: campusId } }),

  saveSection: (token, sectionKey, data, { strict = false, currentStep, signal } = {}) =>
    request('PATCH', `/section/${sectionKey}`, {
      body: { draft_token: token, data, strict, current_step: currentStep },
      signal,
    }),

  skipSection: (token, sectionKey) =>
    request('POST', `/section/${sectionKey}/skip`, { body: { draft_token: token } }),

  uploadPhoto: (token, file) => {
    const form = new FormData();
    form.append('photo', file);
    form.append('draft_token', token);
    return request('POST', '/photo', { formData: form });
  },

  removePhoto: (token) => request('DELETE', '/photo', { body: { draft_token: token } }),

  uploadDocument: (token, file, documentType = 'other') => {
    const form = new FormData();
    form.append('document', file);
    form.append('draft_token', token);
    form.append('document_type', documentType);
    return request('POST', '/documents', { formData: form });
  },

  removeDocument: (token, id) =>
    request('DELETE', `/documents/${id}`, { body: { draft_token: token } }),

  preview: (token) => request('GET', `/preview/${encodeURIComponent(token)}`),

  submit: (token) => request('POST', '/submit', { body: { draft_token: token } }),

  requestResumeLink: (token, email) =>
    request('POST', '/resume-link', { body: { draft_token: token, email } }),

  /** Admin — list interns with onboarding progress */
  adminInterns: () => request('GET', '/admin/interns'),

  /** Admin — universal onboarding entry link (any student) */
  adminPublicInviteLink: () => request('GET', '/admin/invite-link'),

  /** Admin — create a unique link for one student */
  adminCreateInviteLink: () => request('POST', '/admin/invite-link'),

  /** Admin — get or create resume link for an existing intern user */
  adminLink: (userId) => request('POST', `/admin/link/${userId}`),

  /** Admin — view intern onboarding detail */
  adminDetail: (userId) => request('GET', `/admin/${userId}`),

  /** Admin — approve submission (intern can log in) */
  adminAccept: (userId) => request('POST', `/admin/${userId}/accept`),

  /** Admin — reject submission (moves to draft for revision) */
  adminReject: (userId, reason = '') =>
    request('POST', `/admin/${userId}/reject`, { body: { reason } }),

  /** Admin — delete rejected or in-progress draft */
  adminDeleteDraft: (userId) => request('DELETE', `/admin/${userId}/draft`),
};

export function resolveUploadUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const origin = API_BASE.replace(/\/api$/, '');
  if (url.startsWith('/')) return `${origin}${url}`;
  return `${origin}/${url}`;
}
